import { Injectable } from '@angular/core';

import { normalizeProvinceCode } from '../../../../utils/province-code';
import { CustomerAddress } from '../../../model/customer-information.model';
import { FoundCustomer } from '../../../model/found-customer.model';
import {
  GcdmAddress,
  GcdmAddressStatus,
  GcdmBusinessPartner,
  GcdmBusinessPartnerResponse,
  GcdmCommunicationStatus,
  GcdmPhoneCommunicationType,
  GcdmEmailCommunicationType,
  GcdmPartnerCategory,
} from '../model/response.model';

@Injectable({
  providedIn: 'root',
})
export class GcdmFoundCustomerViewModelMapper {
  mapToFoundCustomers(response: GcdmBusinessPartnerResponse): FoundCustomer[] {
    return response.map((businessPartner) => this.mapToFoundCustomer(businessPartner));
  }

  mapToFoundCustomer(data: GcdmBusinessPartner): FoundCustomer {
    return {
      gcid: data.gcid,
      accountNumber: undefined, // Indicates that the customer was loaded from the NSC Database (GCDM)
      bmwMiniId: data.loginIdMail,
      firstName: data.givenName,
      middleName: data.middleName,
      lastName: data.surname,
      // additionalFamilyName, not nameSuffixGeneration: same field the profile mapper reads.
      suffix: data.additionalFamilyName,
      phone: this.getPhone(data, GcdmPhoneCommunicationType.TEL),
      mobile: this.getPhone(data, GcdmPhoneCommunicationType.MOB),
      email: this.getEmail(data),
      address: this.mapAddress(this.getPreferredAddress(data)),
      isIndividual: data.partnerCategory === GcdmPartnerCategory.PERSON,
      customerToVehicleRelations: new Set(),
      taxNumber: this.getTaxNumber(data),
    };
  }

  private mapAddress(address: GcdmAddress | undefined): CustomerAddress | undefined {
    if (!address) {
      return undefined;
    }

    return {
      street: address.street,
      city: address.city,
      houseNumber: address.houseNumber,
      postalCode: address.postalCode,
      province: normalizeProvinceCode(address.regionCode, address.country),
      country: address.country,
    };
  }

  /**
   * Returns the address to display for a search result.
   * GCDM marks exactly one address as globally `preferred`.
   * If none is marked, we fall back to the preferred business address, then the preferred private one,
     then any business address, then any private one - mirroring how ESO CRM results are displayed.
   */
  private getPreferredAddress(data: GcdmBusinessPartner): GcdmAddress | undefined {
    const addresses = data.addresses?.addresses ?? [];
    return (
      addresses.find((address) => address.preferred) ??
      addresses.find((address) => address.preferredFor === GcdmAddressStatus.BUSINESS) ??
      addresses.find((address) => address.preferredFor === GcdmAddressStatus.PRIVATE) ??
      addresses.find((address) => address.addressStatus === GcdmAddressStatus.BUSINESS) ??
      addresses.find((address) => address.addressStatus === GcdmAddressStatus.PRIVATE)
    );
  }

  /**
   * Returns the preferred email address, falling back to the business one, then the private one.
   * EPOST entries are ignored, as they are not email addresses.
   */
  private getEmail(data: GcdmBusinessPartner): string | undefined {
    const emails = (data.communications?.communicationEMails ?? []).filter(
      (email) => email.communicationType === GcdmEmailCommunicationType.EMAIL,
    );
    return (
      emails.find((email) => email.preferred) ??
      emails.find((email) => email.communicationStatus === GcdmCommunicationStatus.BUSINESS) ??
      emails.find((email) => email.communicationStatus === GcdmCommunicationStatus.PRIVATE)
    )?.value;
  }

  /** Returns the preferred phone of the given type, falling back to the business one, then the private one. */
  private getPhone(data: GcdmBusinessPartner, type: GcdmPhoneCommunicationType): string | undefined {
    const phones = (data.communications?.communicationPhones ?? []).filter((phone) => phone.communicationType === type);
    return (
      phones.find((phone) => phone.preferred) ??
      phones.find((phone) => phone.communicationStatus === GcdmCommunicationStatus.BUSINESS) ??
      phones.find((phone) => phone.communicationStatus === GcdmCommunicationStatus.PRIVATE)
    )?.value;
  }

  /**
   * Returns the customer's tax number.
   * GCDM can return tax data for several countries and does not mark a preferred entry.
   * As search results do not display the tax country, the first available number is used,
   * matching how ESO CRM results only ever carry a single tax number.
   */
  private getTaxNumber(data: GcdmBusinessPartner): string | undefined {
    return (data.taxDatas ?? []).find((taxData) => taxData.taxId)?.taxId;
  }
}
